import { defineMessages, type IntlShape } from "react-intl";

import type { Interview } from "@aglocal/schema/Interview";

export type RecordingNoticeContext = "insights" | "skills";

export interface RecordingNotice {
  intent: "warning" | "error";
  title: string;
  body: string;
}

const messages = defineMessages({
  talent: {
    defaultMessage: "The talent",
    description: "Stands in for the talent's name when it is unknown.",
  },
  consentPendingTitle: {
    defaultMessage:
      "{name} has not yet provided consent to be recorded for this meeting.",
    description:
      "Title of the notice shown while the talent has not yet answered the recording consent prompt; {name} is the talent's name.",
  },
  consentPendingSkills: {
    defaultMessage:
      "Please note that if they choose to opt out, transcripts will not be captured. In that case, use the field below to document meeting notes manually.",
    description:
      "Detail of the pending consent notice, shown in the in-meeting sidebar above the notes field.",
  },
  consentPendingInsights: {
    defaultMessage:
      "Please note that if they choose to opt out, automated meeting notes, including skills, job title and organization, and transcripts, will not be captured by the Assistant.",
    description:
      "Detail of the pending consent notice, shown on the pre-meeting insights page.",
  },
  optedOutTitle: {
    defaultMessage: "{name} has opted out of being recorded for this meeting.",
    description:
      "Title of the notice shown when the talent refused to be recorded; {name} is the talent's name.",
  },
  optedOutSkills: {
    defaultMessage:
      "Transcripts will not be captured. Please use the field below to document meeting notes manually.",
    description:
      "Detail of the opted-out notice, shown in the in-meeting sidebar above the notes field.",
  },
  optedOutInsights: {
    defaultMessage:
      "Please note that automated meeting notes, including skills, job title and organization, and transcripts, will not be captured by the Assistant.",
    description:
      "Detail of the opted-out notice, shown on the pre-meeting insights page.",
  },
});

/**
 * Determines the recording consent notice for a given interview.
 * Returns `null` when no notice is needed. The `context` tailors the body copy
 * to where the notice will be displayed.
 */
export default function getRecordingNotice(
  intl: IntlShape,
  interview: Interview,
  context: RecordingNoticeContext,
): RecordingNotice | null {
  const { interviewee, meetNow } = interview;
  const name = interviewee?.displayName || intl.formatMessage(messages.talent);

  if (meetNow && interviewee?.recordOptOut == null) {
    return {
      intent: "warning",
      title: intl.formatMessage(messages.consentPendingTitle, { name }),
      body: intl.formatMessage(
        context === "skills"
          ? messages.consentPendingSkills
          : messages.consentPendingInsights,
      ),
    };
  }

  if (interviewee?.recordOptOut) {
    return {
      intent: "error",
      title: intl.formatMessage(messages.optedOutTitle, { name }),
      body: intl.formatMessage(
        context === "skills"
          ? messages.optedOutSkills
          : messages.optedOutInsights,
      ),
    };
  }

  return null;
}
